/*
 * Icônes inline partagées par les blocs natifs : pas de bibliothèque, pas de
 * dépendance. Règles de la famille, à tenir pour tout nouveau picto :
 *
 * 1. Un seul style : plein, pas de contour ni de trait.
 * 2. La couleur vient toujours du contexte : chaque `<svg>` porte
 *    `fill="currentColor"`, jamais une couleur en dur. C'est l'icône qui le
 *    déclare, pas le CSS du bloc : sans cet attribut le fill par défaut d'un SVG
 *    est noir, et l'icône disparaît sur fond sombre. Le bloc n'a plus qu'à poser
 *    le `color` de la pastille.
 * 3. Le dessin occupe **84 % de sa case**, c'est le viewBox qui l'assure : deux
 *    icônes de provenances différentes pèsent alors le même poids optique à
 *    l'écran. Ces viewBox sont calculés, pas écrits à la main : ils centrent le
 *    dessin et lui laissent sa marge (voir `docs/CHARTE-PAF.md`).
 * 4. `aria-hidden="true"` : une icône ne porte jamais l'information, c'est le
 *    texte à côté qui la porte.
 * 5. La taille se règle dans le CSS du bloc (`width` / `height`), jamais ici.
 *
 * Exceptions assumées : `Stars` (rangée de notation, mise en page à elle),
 * `SparkleIcon` (éclat décoratif positionné au pixel par ClientShowcase) et
 * `SocialIcon` (logos de marque : on ne redessine pas un logo).
 */

import type { ReactElement, ReactNode } from "react";

type IconProps = { className?: string };

export function CalendarIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0.1 0.1 23.81 23.81" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM6 12h4v4H6v-4Z" />
    </svg>
  );
}

/** Calendrier avec une coche : la notification « Nouvelle demande ». */
export function CalendarCheckIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4M9 15l2.2 2.2L15.5 13" />
    </svg>
  );
}

// Étoile Breakdance (la même que dans les blocs capturés) : une seule famille d'étoiles partout.
const STAR =
  "M11.549 3.65c.3-.92 1.603-.92 1.902 0l1.519 4.675a1 1 0 0 0 .95.69h4.915c.97 0 1.372 1.24.588 1.81l-3.976 2.889a1 1 0 0 0-.363 1.118l1.519 4.674c.299.92-.756 1.687-1.54 1.118l-3.975-2.889a1 1 0 0 0-1.176 0l-3.976 2.889c-.783.569-1.838-.197-1.539-1.118l1.519-4.674a1 1 0 0 0-.363-1.118l-3.976-2.89c-.784-.569-.381-1.808.588-1.808h4.914a1 1 0 0 0 .951-.691L11.55 3.65Z";

export function Stars({ count = 5, className }: { count?: number; className?: string }) {
  return (
    <span className={className} role="img" aria-label={`${count} étoiles sur 5`}>
      {/* Taille intrinsèque (20 px) : sans elle, un navigateur qui peint avant le CSS (Mi Browser, réseau lent)
          affiche chaque étoile à la taille par défaut d'un SVG (300 × 150). Le CSS de chaque bloc la remplace. */}
      {Array.from({ length: count }, (_, i) => (
        <svg key={i} width="20" height="20" viewBox="0 0 25 25" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
          <path d={STAR} />
        </svg>
      ))}
    </span>
  );
}

export function BulbIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-128.77 -48.76 609.54 609.54" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M96.06 454.35c.01 6.29 1.87 12.45 5.36 17.69l17.09 25.69a31.99 31.99 0 0 0 26.64 14.28h61.71a31.99 31.99 0 0 0 26.64-14.28l17.09-25.69a31.989 31.989 0 0 0 5.36-17.69l.04-38.35H96.01l.05 38.35zM0 176c0 44.37 16.45 84.85 43.56 115.78 16.52 18.85 42.36 58.23 52.21 91.45.04.26.07.52.11.78h160.24c.04-.26.07-.51.11-.78 9.85-33.22 35.69-72.6 52.21-91.45C335.55 260.85 352 220.37 352 176 352 78.61 272.91-.3 175.45 0 73.44.31 0 82.97 0 176zm176-80c-44.11 0-80 35.89-80 80 0 8.84-7.16 16-16 16s-16-7.16-16-16c0-61.76 50.24-112 112-112 8.84 0 16 7.16 16 16s-7.16 16-16 16z" />
    </svg>
  );
}

export function QuoteIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-3.05 -2.07 38.13 38.13" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M7.031 14c3.866 0 7 3.134 7 7s-3.134 7-7 7-7-3.134-7-7l-0.031-1c0-7.732 6.268-14 14-14v4c-2.671 0-5.182 1.040-7.071 2.929-0.364 0.364-0.695 0.751-0.995 1.157 0.357-0.056 0.724-0.086 1.097-0.086zM25.031 14c3.866 0 7 3.134 7 7s-3.134 7-7 7-7-3.134-7-7l-0.031-1c0-7.732 6.268-14 14-14v4c-2.671 0-5.182 1.040-7.071 2.929-0.364 0.364-0.695 0.751-0.995 1.157 0.358-0.056 0.724-0.086 1.097-0.086z" />
    </svg>
  );
}

export function PhoneIcon({ className }: IconProps) {
  // Combiné filaire (icône du site sur la page contact) : lisible sur fond sombre comme sur fond clair.
  return (
    <svg className={className} viewBox="-2.29 -2.29 28.57 28.57" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.26016 1.289L6.69616 2.061C0.903156 5.081 9.49416 23.005 16.0062 23.005C16.4662 23.005 16.9102 22.911 17.3232 22.721L18.8652 21.966L15.9672 16.372L14.4272 17.126C14.2462 17.213 14.0432 17.26 13.8302 17.26C11.2692 17.26 6.98916 9.056 9.58916 7.664L11.1352 6.901L8.26016 1.289V1.289ZM16.0062 24C10.3262 24 3.78516 12.886 3.78516 6.168C3.78516 3.749 4.61816 2.022 6.24216 1.176L8.62416 0L12.4812 7.347L10.0442 8.548C8.60516 9.32 12.4532 16.972 14.0002 16.228L16.3992 15.049L20.2152 22.409C20.2152 22.409 17.8552 23.571 17.7392 23.624C17.1922 23.875 16.6102 24 16.0062 24"
      />
    </svg>
  );
}

export function MailIcon({ className }: IconProps) {
  // Enveloppe filaire (icône du site sur la page contact).
  return (
    <svg className={className} viewBox="-2.29 -2.29 28.57 28.57" xmlns="http://www.w3.org/2000/svg" fillRule="evenodd" clipRule="evenodd" aria-hidden="true" fill="currentColor">
      <path d="M24 21h-24v-18h24v18zm-23-16.477v15.477h22v-15.477l-10.999 10-11.001-10zm21.089-.523h-20.176l10.088 9.171 10.088-9.171z" />
    </svg>
  );
}

/** Plus / moins de la FAQ (icônes Breakdance de la FAQ du site). */
export function PlusIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="1.33 1.33 20.83 20.83" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="m11 11h-7.25c-.414 0-.75.336-.75.75s.336.75.75.75h7.25v7.25c0 .414.336.75.75.75s.75-.336.75-.75v-7.25h7.25c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-7.25v-7.25c0-.414-.336-.75-.75-.75s-.75.336-.75.75z" />
    </svg>
  );
}

export function MinusIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="1.33 1.33 20.83 20.83" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="m21 11.75c0-.414-.336-.75-.75-.75h-16.5c-.414 0-.75.336-.75.75s.336.75.75.75h16.5c.414 0 .75-.336.75-.75z" />
    </svg>
  );
}

/** Baguette magique, loupe et G de Google : icônes des cartes services de l'accueil. */
export function WandIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-3.05 -3.05 38.1 38.1" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M8 6l-4-4h-2v2l4 4zM10 0h2v4h-2zM18 10h4v2h-4zM20 4v-2h-2l-4 4 2 2zM0 10h4v2h-4zM10 18h2v4h-2zM2 18v2h2l4-4-2-2zM31.563 27.563l-19.879-19.879c-0.583-0.583-1.538-0.583-2.121 0l-1.879 1.879c-0.583 0.583-0.583 1.538 0 2.121l19.879 19.879c0.583 0.583 1.538 0.583 2.121 0l1.879-1.879c0.583-0.583 0.583-1.538 0-2.121zM15 17l-6-6 2-2 6 6-2 2z" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-3.05 -3.05 38.1 38.1" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M31.008 27.231l-7.58-6.447c-0.784-0.705-1.622-1.029-2.299-0.998 1.789-2.096 2.87-4.815 2.87-7.787 0-6.627-5.373-12-12-12s-12 5.373-12 12 5.373 12 12 12c2.972 0 5.691-1.081 7.787-2.87-0.031 0.677 0.293 1.515 0.998 2.299l6.447 7.58c1.104 1.226 2.907 1.33 4.007 0.23s0.997-2.903-0.23-4.007zM12 20c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z" />
    </svg>
  );
}

export function GoogleIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-51.24 -39.24 590.48 590.48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z" />
    </svg>
  );
}

/** Icônes de la mosaïque « Un site web performant » : crayon, cible, taille de texte, fusée, étoile pleine. */
export function EditIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-54.86 -86.81 685.71 685.71" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.3 15.2-40 0-55.2zM384 346.2V448H64V128h229.8c3.2 0 6.2-1.3 8.5-3.5l40-40c7.6-7.6 2.2-20.5-8.5-20.5H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V306.2c0-10.7-12.9-16-20.5-8.5l-40 40c-2.2 2.3-3.5 5.3-3.5 8.5z" />
    </svg>
  );
}

export function TargetIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-3.05 -3.05 38.1 38.1" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M32 14h-3.154c-0.864-5.57-5.276-9.982-10.846-10.846v-3.154h-4v3.154c-5.57 0.864-9.982 5.276-10.846 10.846h-3.154v4h3.154c0.864 5.57 5.276 9.982 10.846 10.846v3.154h4v-3.154c5.57-0.864 9.982-5.276 10.846-10.846h3.154v-4zM24.776 14h-3.118c-0.603-1.705-1.953-3.056-3.658-3.658v-3.118c3.36 0.765 6.010 3.416 6.776 6.776zM16 18c-1.105 0-2-0.895-2-2s0.895-2 2-2c1.105 0 2 0.895 2 2s-0.895 2-2 2zM14 7.224v3.118c-1.705 0.603-3.056 1.953-3.658 3.658h-3.118c0.765-3.36 3.416-6.010 6.776-6.776zM7.224 18h3.118c0.603 1.705 1.953 3.056 3.658 3.658v3.118c-3.36-0.765-6.010-3.416-6.776-6.776zM18 24.776v-3.118c1.705-0.603 3.056-1.953 3.658-3.658h3.118c-0.765 3.36-3.416 6.010-6.776 6.776z" />
    </svg>
  );
}

export function TextSizeIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-0.67 1.33 33.33 33.33" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M2 16h12v4h-4v12h-4v-12h-4zM30 8h-7.867v24h-4.266v-24h-7.867v-4h20z" />
    </svg>
  );
}

export function RocketIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-48.76 -48.76 609.52 609.52" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M505.12019,19.09375c-1.18945-5.53125-6.65819-11-12.207-12.1875C460.716,0,435.507,0,410.40747,0,307.17523,0,245.26909,55.20312,199.05238,128H94.83772c-16.34763.01562-35.55658,11.875-42.88664,26.48438L2.51562,253.29688A28.4,28.4,0,0,0,0,264a24.00867,24.00867,0,0,0,24.00582,24H127.81618l-22.47457,22.46875c-11.36521,11.36133-12.99607,32.25781,0,45.25L156.24582,406.625c11.15623,11.1875,32.15619,13.15625,45.27726,0l22.47457-22.46875V488a24.00867,24.00867,0,0,0,24.00581,24,28.55934,28.55934,0,0,0,10.707-2.51562l98.72834-49.39063c14.62888-7.29687,26.50776-26.5,26.50776-42.85937V312.79688c72.59753-46.3125,128.03493-108.40626,128.03493-211.09376C512.07526,76.5,512.07526,51.29688,505.12019,19.09375ZM384.04033,168A40,40,0,1,1,424.05,128,40.02322,40.02322,0,0,1,384.04033,168Z" />
    </svg>
  );
}

export function StarIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="1.38 0.77 22.23 22.23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d={STAR} />
    </svg>
  );
}

/* Icônes de réassurance : couronne, bouclier, cœur, étiquette, horloge, coche. */
export function CrownIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="1.29 2.2 21.43 21.43" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M3 7.5a1.5 1.5 0 1 1 1.98 1.42l1.6 3.2 3.62-4.53a1.5 1.5 0 1 1 1.6 0l3.62 4.53 1.6-3.2A1.5 1.5 0 1 1 21 7.5c0 .7-.48 1.29-1.13 1.45L18.2 18H5.8L4.13 8.95A1.5 1.5 0 0 1 3 7.5ZM5.8 19.2h12.4a.9.9 0 0 1 0 1.8H5.8a.9.9 0 0 1 0-1.8Z" />
    </svg>
  );
}

export function ShieldIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-0.08 -0.13 24.17 24.17" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M12 1.8 4 4.9v6.3c0 4.7 3.2 9.1 8 10.9 4.8-1.8 8-6.2 8-10.9V4.9l-8-3.1Zm0 2.15 6 2.32v4.93c0 3.7-2.4 7.2-6 8.85-3.6-1.65-6-5.15-6-8.85V6.27l6-2.32Zm3.6 4.7-4.9 4.9-2.3-2.3-1.3 1.3 3.6 3.6 6.2-6.2-1.3-1.3Z" />
    </svg>
  );
}

export function HeartIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0.04 0.57 23.93 23.93" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M12 21s-7.8-4.9-9.6-9.2C1.1 8.6 2.7 5 6.2 4.2c2-.45 4 .3 5.15 1.9l.65.9.65-.9C13.8 4.5 15.8 3.75 17.8 4.2c3.5.8 5.1 4.4 3.8 7.6C19.8 16.1 12 21 12 21Zm0-2.35c2-1.35 6.4-4.6 7.75-7.75.85-2.05-.05-4.3-2.1-4.75-1.4-.3-2.8.25-3.6 1.4L12 10.2 9.95 7.55c-.8-1.15-2.2-1.7-3.6-1.4-2.05.45-2.95 2.7-2.1 4.75C5.6 14.05 10 17.3 12 18.65Z" />
    </svg>
  );
}

export function TagIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-0.13 0.06 24.27 24.27" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M11.6 2H21a1 1 0 0 1 1 1v9.4a1 1 0 0 1-.3.7l-9 9a1 1 0 0 1-1.4 0l-9-9a1 1 0 0 1 0-1.4l9-9a1 1 0 0 1 .3-.7Zm-.2 2.1L4.1 11.4l8.5 8.5 7.4-7.4V4H11.4ZM17 5.6a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2Z" />
    </svg>
  );
}

export function ClockIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0.1 0.1 23.81 23.81" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 1.9a8.1 8.1 0 1 1 0 16.2 8.1 8.1 0 0 1 0-16.2ZM11.1 6.5v6.1l4.7 2.8.9-1.55-3.8-2.25V6.5h-1.8Z" />
    </svg>
  );
}

export function CheckIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0.1 0.1 23.81 23.81" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 1.9a8.1 8.1 0 1 1 0 16.2 8.1 8.1 0 0 1 0-16.2Zm4.4 4.8-6.1 6.1-2.7-2.7-1.35 1.35 4.05 4.05 7.45-7.45L16.4 8.7Z" />
    </svg>
  );
}

export function CrossIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0.1 0.1 23.81 23.81" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 1.9a8.1 8.1 0 1 1 0 16.2 8.1 8.1 0 0 1 0-16.2Z" />
      <path d="M15.6 9.75 14.25 8.4 12 10.65 9.75 8.4 8.4 9.75 10.65 12 8.4 14.25 9.75 15.6 12 13.35 14.25 15.6 15.6 14.25 13.35 12Z" />
    </svg>
  );
}

export function PinIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="-3.05 -3.05 38.1 38.1" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M16 0c-5.523 0-10 4.477-10 10 0 10 10 22 10 22s10-12 10-22c0-5.523-4.477-10-10-10zM16 16c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z" />
    </svg>
  );
}

/** L'éclat doodle du site (quatre traits en éventail), recadré sur le dessin ; hérite de currentColor. */
export function SparkleIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="60 48 54 72" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d="M67.3814 54.1679C67.3555 54.8911 67.3203 55.6174 67.3048 56.3458C67.2971 56.7307 67.2821 57.1198 67.3356 57.5015C67.4803 58.5171 67.6354 59.5378 67.7677 60.5678C67.9011 61.5958 68.0074 62.6363 68.0588 63.6831C68.1096 64.9758 68.125 66.2769 68.0347 67.5926L68.0074 68.0844L67.9605 68.5794C67.9288 68.9084 67.8992 69.2383 67.8603 69.5663C67.7648 70.2263 67.6849 70.8863 67.5448 71.5474C67.5077 71.7467 67.4365 71.9418 67.3506 72.1297C67.2648 72.3177 67.1447 72.5015 67.0371 72.6812C66.8218 73.0249 66.3406 73.1533 65.7533 73.1459C64.8984 73.1295 64.0926 72.6815 63.9963 72.1774C63.9346 71.8538 64.0234 71.5455 64.1205 71.2465C64.2393 70.904 64.3549 70.5624 64.4301 70.2085C64.5033 69.8536 64.5577 69.4945 64.5987 69.1365C64.6241 68.7733 64.6308 68.406 64.6229 68.0419C64.6151 67.6777 64.5668 67.3063 64.5165 66.9391C64.464 66.5346 64.3274 66.1115 64.4544 65.7211C64.8457 64.4672 64.5803 63.1842 64.6582 61.8985C64.6917 61.4202 64.6796 60.9398 64.657 60.4595C64.6397 59.9791 64.6047 59.4978 64.5998 59.0132C64.5733 57.2868 64.5458 55.5469 64.5058 53.8008C64.4902 53.1036 64.6946 52.4227 65.3327 51.7867C65.629 51.4937 66.3947 51.45 66.5933 51.7433C66.8761 52.1589 67.0677 52.5965 67.2064 53.0414C67.32 53.4064 67.2916 53.7966 67.3284 54.1752C67.3471 54.1741 67.3627 54.1741 67.3814 54.173L67.3814 54.1679Z" />
      <path d="M98.5473 60.6191C97.7307 61.2803 96.9069 61.9352 96.1111 62.6274C95.6852 62.988 95.2625 63.3581 94.8917 63.7747L94.1542 64.61L93.4324 65.4608C92.9497 66.0239 92.4858 66.6067 92.0156 67.1811C91.5423 67.7515 91.0919 68.3435 90.6259 68.9201L89.2486 70.6653C88.1293 72.106 87.0078 73.5458 85.9289 75.0145C84.849 76.4853 83.7847 77.9664 82.7744 79.4848C82.4648 79.9398 82.0515 80.3326 81.6495 80.7233C81.261 81.0922 80.7091 81.1439 80.1113 81.0172C79.2384 80.8338 78.6511 80.0981 78.8844 79.3434C79.0331 78.868 79.3095 78.4173 79.6326 78.034C81.0803 76.241 82.3567 74.36 83.4919 72.3517C83.8002 71.7951 84.0939 71.1898 84.5654 70.7741C85.3052 70.1058 85.9525 69.363 86.5718 68.5984C87.2056 67.8463 87.8072 67.0651 88.5117 66.3741C89.0286 65.8566 89.5186 65.3153 90.0189 64.7792L90.7626 63.9729C91.0153 63.71 91.2712 63.4461 91.5313 63.1894C93.3836 61.3543 95.3201 59.6104 97.2919 57.9203C98.0753 57.2427 98.9834 56.7827 100.08 56.7198C100.586 56.6921 101.076 57.2858 100.852 57.7188C100.54 58.3356 100.142 58.8944 99.721 59.4231C99.3751 59.8574 98.9191 60.1953 98.5161 60.5777C98.5275 60.5912 98.5369 60.6036 98.5473 60.6191Z" />
      <path d="M107.662 91.1743C107.123 91.2063 106.579 91.231 106.044 91.2857C105.759 91.3121 105.474 91.3385 105.207 91.4312C104.496 91.6782 103.803 91.9656 103.118 92.2541L101.089 93.138C100.26 93.4952 99.4462 93.8699 98.6365 94.2571C97.835 94.6588 97.0326 95.0626 96.2614 95.5234C96.024 95.6566 95.7242 95.6913 95.4275 95.7249C95.1402 95.7555 94.7631 95.4769 94.3589 95.0553C93.7707 94.4409 93.4146 93.6104 93.6433 93.2189C93.7871 92.9717 94.0193 92.8178 94.2713 92.7282C95.4244 92.2969 96.4496 91.7009 97.4164 90.915C97.6838 90.7009 97.9438 90.4234 98.2913 90.4022C99.3847 90.3144 100.263 89.7279 101.287 89.523C101.667 89.4529 102.033 89.3423 102.4 89.2401C102.767 89.1327 103.133 89.0273 103.505 88.9531C104.836 88.6803 106.17 88.4946 107.491 88.3131C108.019 88.2417 108.564 88.3913 109.14 88.9684C109.406 89.2356 109.553 89.9907 109.364 90.2099C109.094 90.5216 108.791 90.7462 108.48 90.9179C108.224 91.0573 107.929 91.0608 107.653 91.1256C107.657 91.1432 107.659 91.1598 107.664 91.1774L107.662 91.1743Z" />
      <path d="M108.265 115.843C107.73 115.628 107.194 115.413 106.654 115.222C106.367 115.121 106.082 115.023 105.784 114.986C105.388 114.939 104.992 114.915 104.596 114.884C104.201 114.866 103.809 114.841 103.416 114.833C102.634 114.816 101.859 114.806 101.092 114.816C100.146 114.811 99.2081 114.852 98.2692 114.926L97.5669 114.988C97.3314 115.008 97.1001 115.046 96.8657 115.074C96.3968 115.126 95.9331 115.214 95.4664 115.292C95.1853 115.336 94.8781 115.282 94.5698 115.214C94.275 115.15 94.0006 114.764 93.7507 114.235C93.3864 113.463 93.3301 112.561 93.7052 112.261C93.9404 112.07 94.2329 112.003 94.5182 111.992C95.84 111.966 97.093 111.745 98.379 111.359C98.7336 111.261 99.1069 111.105 99.464 111.205C100.591 111.549 101.712 111.345 102.812 111.582C103.215 111.677 103.622 111.734 104.029 111.792C104.433 111.861 104.841 111.911 105.235 112.009C106.654 112.34 108.022 112.772 109.371 113.196C109.911 113.366 110.367 113.741 110.67 114.517C110.81 114.876 110.625 115.622 110.341 115.734C109.942 115.896 109.543 115.964 109.167 115.979C108.857 115.993 108.568 115.859 108.272 115.798C108.267 115.813 108.264 115.83 108.258 115.847L108.265 115.843Z" />
    </svg>
  );
}

export type SocialNetwork = "facebook" | "x" | "instagram" | "linkedin" | "youtube";

const SOCIAL: Record<SocialNetwork, { viewBox: string; path: string }> = {
  facebook: {
    viewBox: "0 0 24 24",
    path: "M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z",
  },
  x: {
    viewBox: "0 0 512 512",
    path: "M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z",
  },
  instagram: {
    viewBox: "0 0 24 24",
    path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
  },
  linkedin: {
    viewBox: "-4 -2 32 32",
    path: "M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z",
  },
  youtube: {
    viewBox: "0 0 24 24",
    path: "M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z",
  },
};

export function SocialIcon({ network, className }: { network: SocialNetwork; className?: string }) {
  const icon = SOCIAL[network];
  return (
    <svg className={className} viewBox={icon.viewBox} xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor">
      <path d={icon.path} />
    </svg>
  );
}

/**
 * Nom d'icône → icône. Un seul endroit où la correspondance existe : deux blocs
 * qui acceptent `icon: "clock"` affichent alors exactement le même dessin.
 * Un bloc peut toujours recevoir un SVG à la place d'un nom.
 */
export type IconName =
  | "bulb"
  | "calendar"
  | "check"
  | "clock"
  | "cross"
  | "crown"
  | "edit"
  | "google"
  | "heart"
  | "mail"
  | "phone"
  | "pin"
  | "quote"
  | "rocket"
  | "search"
  | "shield"
  | "star"
  | "tag"
  | "target"
  | "text"
  | "wand";

const ICONS: Record<IconName, (props: IconProps) => ReactElement> = {
  bulb: BulbIcon,
  calendar: CalendarIcon,
  check: CheckIcon,
  clock: ClockIcon,
  cross: CrossIcon,
  crown: CrownIcon,
  edit: EditIcon,
  google: GoogleIcon,
  heart: HeartIcon,
  mail: MailIcon,
  phone: PhoneIcon,
  pin: PinIcon,
  quote: QuoteIcon,
  rocket: RocketIcon,
  search: SearchIcon,
  shield: ShieldIcon,
  star: StarIcon,
  tag: TagIcon,
  target: TargetIcon,
  text: TextSizeIcon,
  wand: WandIcon,
};

/** Rend l'icône si `icon` est un nom connu, le nœud tel quel sinon. */
export function renderIcon(icon: IconName | ReactNode): ReactNode {
  if (typeof icon === "string" && icon in ICONS) {
    const Icon = ICONS[icon as IconName];
    return <Icon />;
  }
  return icon;
}
